import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    adminGetSubscriptionPlans,
    adminGetUserSubscriptions,
} from "../../api/subscriptionApi";
import { getFornitori } from "../../Admin/adminApi";
import "../../Admin/AdminStyles.css";

const euro = (cents) =>
    Number(cents || 0).toLocaleString("it-IT", {
        style: "currency",
        currency: "EUR",
    });

function formatDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("it-IT");
}

const STATUS_LABELS = {
    active: "Attivo",
    paused: "In pausa",
    canceled: "Cancellato",
    cancel_at_period_end: "Cancella a fine periodo",
};

const MODEL_LABELS = {
    discount: "Sconto",
    credits: "Crediti",
    recurring: "Ricorrente",
};

export default function AdminSubscriptionsList() {
    const token = localStorage.getItem("token");

    const [items, setItems] = useState([]);
    const [plans, setPlans] = useState([]);
    const [total, setTotal] = useState(0);
    const [suppliers, setSuppliers] = useState([]);

    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        q: "",
        status: "",
        plan_id: "",
        supplier_id: "",
        page: 1,
        page_size: 20,
    });

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(total / Number(filters.page_size || 20)));
    }, [total, filters.page_size]);

    async function loadPlans() {
        try {
            const data = await adminGetSubscriptionPlans(token, {
                includeInactive: true,
            });
            setPlans(data?.plans || []);
        } catch (err) {
            console.error(err);
            setPlans([]);
        }
    }

    async function loadSubscriptions(nextFilters = filters) {
        if (!token) return;

        setLoading(true);
        try {
            const data = await adminGetUserSubscriptions(token, nextFilters);
            setItems(Array.isArray(data?.items) ? data.items : []);
            setTotal(Number(data?.total || 0));
        } catch (err) {
            toast.error(err.message || "Errore caricamento sottoscrizioni");
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }

    async function loadSuppliers() {
        try {
            const data = await getFornitori();
            setSuppliers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setSuppliers([]);
        }
    }

    useEffect(() => {
        loadPlans();
        loadSuppliers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadSubscriptions(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.page, filters.page_size]);

    function updateFilter(key, value) {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            page: key === "page" ? value : 1,
        }));
    }

    function handleSubmit(e) {
        e.preventDefault();
        loadSubscriptions({ ...filters, page: 1 });
    }

    function clearFilters() {
        const next = {
            q: "",
            status: "",
            plan_id: "",
            supplier_id: "",
            page: 1,
            page_size: filters.page_size,
        };
        setFilters(next);
        loadSubscriptions(next);
    }

    return (
        <div className="admin-page">
            <div className="admin-card">
                <div className="admin-card-header">
                    <div>
                        <h1 className="admin-title">Abbonamenti · Sottoscrizioni</h1>
                        <p className="admin-subtitle">
                            Elenco degli abbonamenti attivati dai clienti.
                        </p>
                    </div>
                </div>

                <form className="admin-form-grid" onSubmit={handleSubmit}>
                    <div className="admin-form-field">
                        <label>Cerca</label>
                        <input
                            value={filters.q}
                            onChange={(e) => updateFilter("q", e.target.value)}
                            placeholder="Cliente, email, fornitore, piano..."
                        />
                    </div>

                    <div className="admin-form-field">
                        <label>Stato</label>
                        <select
                            value={filters.status}
                            onChange={(e) => updateFilter("status", e.target.value)}
                        >
                            <option value="">Tutti</option>
                            <option value="active">Attivo</option>
                            <option value="paused">In pausa</option>
                            <option value="cancel_at_period_end">
                                Cancella a fine periodo
                            </option>
                            <option value="canceled">Cancellato</option>
                        </select>
                    </div>

                    <div className="admin-form-field">
                        <label>Piano</label>
                        <select
                            value={filters.plan_id}
                            onChange={(e) => updateFilter("plan_id", e.target.value)}
                        >
                            <option value="">Tutti i piani</option>
                            {plans.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name} · {MODEL_LABELS[p.model_code] || p.model_code}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="admin-form-field">
                        <label>Fornitore</label>
                        <select
                            value={filters.supplier_id}
                            onChange={(e) => updateFilter("supplier_id", e.target.value)}
                        >
                            <option value="">Tutti i fornitori</option>
                            {suppliers.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.nome || `Fornitore #${s.id}`}
                                    {s.citta ? ` · ${s.citta}` : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="admin-form-field">
                        <label>Risultati per pagina</label>
                        <select
                            value={filters.page_size}
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    page_size: Number(e.target.value),
                                    page: 1,
                                }))
                            }
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>

                    <div className="admin-form-actions admin-form-field-full">
                        <button type="submit" className="admin-btn admin-btn-primary">
                            Cerca
                        </button>
                        <button type="button" className="admin-btn" onClick={clearFilters}>
                            Pulisci filtri
                        </button>
                    </div>
                </form>
            </div>

            <div className="admin-card">
                <div className="admin-card-header admin-card-header-inline">
                    <div>
                        <h2 className="admin-section-title">Risultati</h2>
                        <p className="admin-subtitle">
                            Totale: <strong>{total}</strong>
                        </p>
                    </div>

                    <div className="admin-inline-filters">
                        <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            disabled={filters.page <= 1 || loading}
                            onClick={() =>
                                setFilters((prev) => ({
                                    ...prev,
                                    page: Math.max(1, prev.page - 1),
                                }))
                            }
                        >
                            ← Precedente
                        </button>

                        <span>
                            Pagina {filters.page} / {totalPages}
                        </span>

                        <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            disabled={filters.page >= totalPages || loading}
                            onClick={() =>
                                setFilters((prev) => ({
                                    ...prev,
                                    page: Math.min(totalPages, prev.page + 1),
                                }))
                            }
                        >
                            Successiva →
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="admin-empty-box">Caricamento sottoscrizioni...</div>
                ) : items.length === 0 ? (
                    <div className="admin-empty-box">Nessuna sottoscrizione trovata.</div>
                ) : (
                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Cliente</th>
                                    <th>Fornitore</th>
                                    <th>Piano</th>
                                    <th>Modello</th>
                                    <th>Prezzo</th>
                                    <th>Stato</th>
                                    <th>Inizio</th>
                                    <th>Fine periodo</th>
                                    <th>Stripe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((sub) => {
                                    const fullName =
                                        [sub.user_name, sub.user_last_name]
                                            .filter(Boolean)
                                            .join(" ") || `Utente #${sub.user_id}`;

                                    return (
                                        <tr key={sub.id}>
                                            <td>{sub.id}</td>

                                            <td>
                                                <div style={{ fontWeight: 700 }}>{fullName}</div>
                                                <div style={{ fontSize: 12, opacity: 0.75 }}>
                                                    {sub.user_email || "—"}
                                                </div>
                                            </td>

                                            <td>
                                                <div>{sub.supplier_name || `#${sub.supplier_id}`}</div>
                                                <div style={{ fontSize: 12, opacity: 0.75 }}>
                                                    ID {sub.supplier_id}
                                                </div>
                                            </td>

                                            <td>
                                                <div style={{ fontWeight: 700 }}>{sub.plan_name}</div>
                                                <div style={{ fontSize: 12, opacity: 0.75 }}>
                                                    ID {sub.plan_id}
                                                </div>
                                            </td>

                                            <td>
                                                {MODEL_LABELS[sub.model_code] ||
                                                    sub.model_name ||
                                                    sub.model_code}
                                            </td>

                                            <td>{euro(sub.price_cents)} / mese</td>

                                            <td>
                                                <span
                                                    className={`admin-status-pill admin-status-pill--${sub.status}`}
                                                >
                                                    {STATUS_LABELS[sub.status] || sub.status}
                                                </span>
                                            </td>

                                            <td>{formatDate(sub.start_date)}</td>
                                            <td>{formatDate(sub.current_period_end)}</td>

                                            <td>
                                                {sub.stripe_subscription_id ? (
                                                    <code>{sub.stripe_subscription_id}</code>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}